import { Box, Button, Paper, Typography, Switch } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { useState, useEffect, useCallback, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "./../../services/services";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import DehazeIcon from "@mui/icons-material/Dehaze";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { flex, minWidth } from "@mui/system";

const PASSWORD_REGEX = /^[A-Za-z0-9]{8,32}$/;

const Users = () => {
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [loading, setLoading] = useState(true);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const navigate = useNavigate();

  const passwordError = useMemo(() => {
    if (!password) return "";
    if (!PASSWORD_REGEX.test(password))
      return "รหัสผ่านต้องเป็นตัวอักษร/ตัวเลข 8–32 ตัว (ห้ามมีช่องว่าง/อักขระพิเศษ)";
    return "";
  }, [password]);
  const confirmError = useMemo(() => {
    if (!confirm) return "";
    if (confirm !== password) return "ยืนยันรหัสผ่านไม่ตรงกัน";
    return "";
  }, [confirm, password]);

  const handleToggleUser = async (row) => {
    try {
      const newStatus = !row.isDisabled;
      await services.updateUserStatus(row.userId, {
        isDisabled: newStatus,
      });

      toast.success(newStatus ? "ปิดการใช้งานแล้ว" : "เปิดการใช้งานแล้ว");
      setRows((prev) =>
        prev.map((r) =>
          r.userId === row.userId ? { ...r, isDisabled: newStatus } : r,
        ),
      );
    } catch (err) {
      console.error(err);
      toast.error("เปลี่ยนสถานะไม่สำเร็จ");
    }
  };

  const columns = [
    { field: "id", headerName: "ลำดับ", flex: 0, width: 70 },
    {
      field: "username",
      headerName: "Email",
      minWidth: 100,
      flex: 1,
      editable: false,
    },
    {
      field: "title",
      headerName: "คำนำหน้า",
      flex: 0.25,
      minWidth: 100,
      editable: true,
    },
    {
      field: "name",
      headerName: "ชื่อ",
      flex: 1,
      minWidth: 100,
      editable: true,
    },
    {
      field: "lastname",
      headerName: "นามสกุล",
      flex: 1,
      minWidth: 100,
      editable: true,
    },
    {
      field: "hospital",
      headerName: "โรงพยาบาล",
      flex: 1.5,
      minWidth: 200,
      display: "flex",
      align: "center",
      headerAlign: "center",
      alignContent: "center",
      sortable: false,
      filterable: false,
      flex: 0,
    },

    {
      field: "role",
      headerName: "บทบาท",
      flex: 1,
      minWidth: 200,
      display: "flex",
      align: "center",
      headerAlign: "center",
      alignContent: "center",
      sortable: false,
      filterable: false,
      flex: 0,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        return [
          <GridActionsCellItem
            key={`${id}-delete`}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="error"
          />,
        ];
      },
    },
    {
      field: "Detail",
      headerName: "รายละเอียด",
      flex: 0.5,
      minWidth: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          startIcon={<DehazeIcon />}
          onClick={() => handleOpenDetail(row)}
        >
          รายละเอียด
        </Button>
      ),
    },
    {
      field: "status",
      headerName: "การใช้งาน",
      flex: 0.5,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Switch
            checked={!row.isDisabled}
            onChange={() => handleToggleUser(row)}
            color="success"
          />
        );
      },
    },
  ];

  const handleOpenDetail = (row) => {
    navigate(`/users/${row.userId}`);
  };
  const handleAddUser = (row) => {
    navigate(`/users/register`);
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    Swal.fire({
      customClass: {
        container: "my-swal",
      },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteUser({ id: deletedRow.userId })
          .then((response) => {
            console.log("User deleted successfully:", response);
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: {
                container: "my-swal",
              },
              title: "ลบแล้ว!",
              text: `${deletedRow.name} ${deletedRow.lastname} ถูกลบแล้ว`,
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            fetchData();
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting user:", error);
          });
      }
    });
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const fetchData = useCallback(async () => {
    try {
      const res = await services.getUser();
      if (res.data.success) {
        const usersWithIndex = res.data.data.map((item, index) => ({
          id: index + 1,
          userId: item.id,
          username: item.username,
          title: item.title,
          name: item.name,
          lastname: item.lastname,
          roleId: item.roleId,
          hospitalId: item.hospitalId,
          hospitalId: item.hospitalId,
          role: item.role.title,
          hospital: item.hospital.name,
          isDisabled: item.isDisabled,
          responsibleBranches: item.responsibleBranches ?? [],
        }));
        setRows(usersWithIndex);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <AppBreadcrumbs />

      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          จัดการบุคลากร
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddUser()}
        >
          เพิ่มบุคลากร
        </Button>
      </Box>
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              loading={loading}
              slotProps={{
                toolbar: { setRows, setRowModesModel },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{ pagination: { page: 0, pageSize: 50 } }}
              pageSizeOptions={[25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default Users;
